/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { NUMBER_OF_APPS_IN_PAGE, STATUS_PUBLISHED } from '../../constants';

import {
  ApplicationsItemType,
  TApplicationsProps,
  useAPIFetchApplicationsPublic,
  useAPIFetchApplicationLaunchTypes,
  useAPIFetchCategories,
  useAPIFetchProducts,
} from '@marketplace/api';

import { changesRequestParameters } from './helpers';

import { EventType } from './app-gallery-page.types';
import { EmbedModeContext } from '../../../features';
import { any } from 'joi';

export default function useAppGalleryPage() {
  const { data: categories,patientCategories, loaded: isCategoriesLoaded } =
    useAPIFetchCategories();
  const {
    data: solutions,
    loaded: isSolutionsLoaded,
    fetchProducts,
  } = useAPIFetchProducts({
    silence: true,
  });
  const {
    data: appTypes,
    loaded: isAppTypesLoaded,
    fetchApplicationLaunchTypes,
  } = useAPIFetchApplicationLaunchTypes({
    silence: true,
  });
  const {
    data: listApplications,
    fetchApplicationsPublic,
    loading: isAllApplicationsLoading,
  } = useAPIFetchApplicationsPublic();
  const { state: embedModeState, changeApplication } =
    useContext(EmbedModeContext);

  const [dataListApplications, setDataListApplications] = useState<
    ApplicationsItemType[] | []
  >([]);
  const [selectedCategories, setCategories] = useState<string[]>([]);
  const [selectedTypes, setTypes] = useState<string[]>([]);
  const [selectedProducts, setProducts] = useState<string[]>([]);
  const [selectedFacing, setFacing ] = useState<string[]>([]);

  const fetchAppsWithFilters = (
    selectedCategories: string[],
    selectedTypes: string[],
    selectedProducts: string[],
    selectedFacing:string[],
    options?: {
      launch_types?: any[];
      products?: any[];
    }
  ) => {
    const launch_app_types = options?.launch_types?.length
      ? options?.launch_types
      : appTypes;
    const products_solutions = options?.products?.length
      ? options?.products
      : solutions;

    const data: TApplicationsProps = {
      page: String(1),
      per_page: String(NUMBER_OF_APPS_IN_PAGE),
      status: STATUS_PUBLISHED,
    };

    if (embedModeState?.tenantId) {
      data.tenants = embedModeState.tenantId;
    }

    if (embedModeState?.types) {
        data.types = embedModeState?.types;
    }
    
    if (embedModeState?.productName) {
      const product = products_solutions.find(
        ({ name }: any) => name === embedModeState.productName
      );

      if (product) {
        data.products = product.id;
      }
    }
    if (embedModeState?.facingName){
      data.facing=embedModeState.facingName;
    }
    if (embedModeState?.oid){
      data.oid=embedModeState.oid;
    }

    changesRequestParameters(
      data,
      selectedCategories,
      selectedTypes,
      selectedProducts,
      selectedFacing
    );
    setDataListApplications([]);

    fetchApplicationsPublic(data);
  };
  const setSelectedCategories = (data: any) => {
    setCategories(data);
    fetchAppsWithFilters(data, selectedTypes, selectedProducts,selectedFacing);
  };
  const setSelectedTypes = (data: any) => {
    setTypes(data);
    fetchAppsWithFilters(selectedCategories, data, selectedProducts,selectedFacing);
  };

  const setSelectedProducts = (data: any) => {
    setProducts(data);
    fetchAppsWithFilters(selectedCategories, selectedTypes, data,selectedFacing);
  };

  useEffect(() => {
    //@ts-ignore
    const comparedApps = dataListApplications.reduce(
      (acc: ApplicationsItemType[] | [], current: ApplicationsItemType) => {
        const findedApp = embedModeState?.apps.find(
          (i) => i.appId === current.id
        );

        if (findedApp) {
          return [...acc, { ...current, status: findedApp.status }];
        }

        return acc;
      },
      []
    );

    if (comparedApps?.length) {
      changeApplication &&
        comparedApps.forEach((app: any) => {
          changeApplication(changeApplication?.transformAPIToEMData(app), {
            silence: true,
          });
        });
    }
  }, [dataListApplications]);

  useEffect(() => {
    if (listApplications?.items) {
      setDataListApplications([
        ...dataListApplications,
        ...listApplications.items,
      ]);
    }
    // eslint-disable-next-line
  }, [listApplications]); // need to avoid adding dataListApplications to the dep.arr, coz it cause re-renders issues

  useEffect(() => {
    (async () => {
      const [products, launch_types] = await Promise.all<any>([
        fetchProducts(),
        fetchApplicationLaunchTypes(),
      ]);

      if (embedModeState && products?.length && launch_types?.length) {
        fetchAppsWithFilters(
          selectedCategories,
          selectedTypes,
          selectedProducts,
          selectedFacing,
          { launch_types, products }
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!embedModeState) {
      fetchApplicationsPublic({
        page: String(1),
        per_page: String(NUMBER_OF_APPS_IN_PAGE),
        status: STATUS_PUBLISHED,
      });
    }
  }, []);

  const handleFetchAllApps = useCallback(() => {
    const data: TApplicationsProps = {
      page: listApplications.page + 1,
      per_page: String(NUMBER_OF_APPS_IN_PAGE),
      status: STATUS_PUBLISHED,
    };
    if (embedModeState?.tenantId) {
      data.tenants = embedModeState.tenantId;
    }

    if (embedModeState?.productName) {
      const product = solutions.find(
        ({ name }: any) => name === embedModeState.productName
      );

      if (product) {
        data.products = product.id;
      }
    }

    if (embedModeState?.types) {
      const type = appTypes.find(
        ({ name }: any) => name === embedModeState.types
      );

      if (type) {
        data.types = type.id;
      }
    }

    if (embedModeState?.facingName){
      data.facing=embedModeState.facingName;
    }
    if (embedModeState?.oid){
      data.oid=embedModeState.oid;
    }

    changesRequestParameters(
      data,
      selectedCategories,
      selectedTypes,
      selectedProducts,
      selectedFacing
    );
    fetchApplicationsPublic(data);
  }, [
    fetchApplicationsPublic,
    selectedProducts,
    selectedTypes,
    selectedCategories,
    dataListApplications.length,
  ]);

  const onCategoriesChange = useCallback(
    (
      e: EventType,
      attributesArr: string[],
      setFunction: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
      const arrayOfSelectedAttributes = [...attributesArr];

      if (e.checked) {
        arrayOfSelectedAttributes.push(e.value);
      } else {
        arrayOfSelectedAttributes.splice(
          arrayOfSelectedAttributes.indexOf(e.value),
          1
        );
      }

      setFunction(arrayOfSelectedAttributes);
    },
    []
  );

  const setSelectedFacing = (data:any) => {
    setFacing(data);
    fetchAppsWithFilters(selectedCategories, selectedTypes, selectedProducts,data);
  }

  const facing = [
    { name: "Patient", value: "PATIENT"},
    { name: "Provider", value: "PROVIDER"},
  ]

  const onFacingChange = useCallback(
    (
      e: EventType,
      attributesArr: string[],
      setFunction: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
      const arrayOfSelectedFacing = [...attributesArr];

      if (e.checked) {
        arrayOfSelectedFacing.push(e.value);
      } else {
        arrayOfSelectedFacing.splice(
          arrayOfSelectedFacing.indexOf(e.value),
          1
        );
      }

      setFunction(arrayOfSelectedFacing);
    },
    []
  );

  return {
    categories,
    patientCategories,
    solutions,
    appTypes,
    isCategoriesLoaded,
    isSolutionsLoaded,
    isAppTypesLoaded,
    isAllApplicationsLoading,
    onCategoriesChange,
    selectedCategories,
    selectedTypes,
    selectedProducts,
    setSelectedProducts,
    setSelectedTypes,
    setSelectedCategories,
    handleFetchAllApps,
    allAppsData: dataListApplications,
    totalAppsCount: listApplications?.total,
    hasNext: listApplications?.has_next,
    facing,
    selectedFacing,
    setSelectedFacing,
    onFacingChange
  };
}
